import { legacyGet, legacyPost } from '../legacy-client';
import type {
  LegacyOrder,
  OrdersResponse,
  OrderResponse,
  CreateOrderDto,
  UpdateOrderDto,
  SuccessResponse,
} from './types';

export const legacyOrdersApi = {
  /**
   * Get orders for a specific client
   */
  getByClient: async (
    clientName: string,
    archive: 0 | 1 = 0,
    limit = 100,
  ): Promise<LegacyOrder[]> => {
    const response = await legacyGet<OrdersResponse>('getOrders', {
      client: clientName,
      archive,
      limit,
    });
    return response.orders || [];
  },

  /**
   * Get all orders (for current user or all if manager)
   */
  getAll: async (archive: 0 | 1 = 0, limit = 100): Promise<LegacyOrder[]> => {
    const response = await legacyGet<OrdersResponse>('getOrders', {
      archive,
      limit,
    });
    return response.orders || [];
  },

  /**
   * Get single order by ID
   */
  getById: async (id: number): Promise<LegacyOrder | null> => {
    const response = await legacyGet<OrderResponse>('getOrder', { id });
    return response.order || null;
  },

  /**
   * Create a new order
   */
  create: (dto: CreateOrderDto): Promise<SuccessResponse> =>
    legacyPost<SuccessResponse, CreateOrderDto>('createOrder', dto),

  /**
   * Update an existing order
   */
  update: (id: number, dto: UpdateOrderDto): Promise<SuccessResponse> =>
    legacyPost<SuccessResponse, UpdateOrderDto & { id: number }>('updateOrder', { ...dto, id }),

  /**
   * Archive an order
   */
  archive: (id: number): Promise<SuccessResponse> =>
    legacyPost<SuccessResponse, { id: number; varhive: number }>('updateOrder', { id, varhive: 1 }),

  /**
   * Request invoice refresh
   */
  refreshSchet: (id: number): Promise<SuccessResponse> =>
    legacyGet<SuccessResponse>('orderRefreshSchet', { id }),

  /**
   * Add comment/clarification to order
   * Note: id is passed via query param, comment via body
   */
  addComment: async (id: number, comment: string): Promise<SuccessResponse> => {
    const { legacyApi } = await import('../legacy-client');
    const { data } = await legacyApi.post<SuccessResponse>(
      `/api_bridge.php?cmd=orderComment&id=${id}`,
      { comment }
    );
    return data;
  },

  /**
   * Send warranty certificate via email
   * Note: id is passed via query param, email via body
   */
  sendGuarantee: async (id: number, email: string): Promise<SuccessResponse> => {
    const { legacyApi } = await import('../legacy-client');
    const { data } = await legacyApi.post<SuccessResponse>(
      `/api_bridge.php?cmd=sendGuarantee&id=${id}`,
      { email }
    );
    return data;
  },
};
